/* eslint complexity: ["error", { max: 8 }] -- applies to entire file*/
import { useState } from "react";

import isEmpty from "lodash/isEmpty";

import { ModalTestID, TextTestID } from "@/constants/TestID";
import DeclineGroupEventConfirmationAlert from "@/ui/components/schedule/confirmationAlerts/DeclineGroupEventConfirmationAlert";
import UnregisterGroupConfirmationAlert from "@/ui/components/schedule/confirmationAlerts/UnregisterGroupConfirmationAlert";
import BottomSheetModal from "@/ui/composed/modal/BottomSheetModal";
import { Button, FAIconName, Text } from "@/ui/core";
import { VStack } from "@/ui/layout";
import { isNotNilNotEmpty } from "@/utils/shared.utils";
import { joinTestIDs } from "@/utils/string.util";

enum EditGroupEventRegistrationContent {
  DeclineCTA = "Decline this event",
  Description = "What would you like to edit?",
  DismissAccessibilityLabel = "Dismiss Edit Group Registration Modal",
  Title = "Edit registration",
  UnregisterCTA = "Unregister from this group",
}

/**
 * Props for the EditGroupEventBottomSheetModal component.
 *
 * @property eventExternalId - The unique identifier for the event, used to decline the event.
 * @property eventTitle - The title of the event, used for analytics purposes.
 * @property forWhom - (Optional) Specifies for whom the unregister confirmation data is relevant.
 * @property groupClassSeriesExternalId - Identifier for the group class series; used to unregister from the entire series. Required for internal logic, but optional in the parent component.
 * @property isOpen - Indicates whether the bottom sheet modal is open.
 * @property onClose - Callback function invoked when the modal is closed.
 * @property onCompletion - Callback function invoked upon completion of the modal's main action.
 * @property patientExternalId - The external identifier for the patient.
 */
type EditGroupEventBottomSheetModalProps = {
  eventExternalId?: string;
  eventTitle: string;
  forWhom?: string;
  groupClassSeriesExternalId: string;
  isOpen: boolean;
  onClose: () => void;
  onCompletion: () => void;
  patientExternalId: string;
};

const EditGroupEventBottomSheetModal = ({
  eventExternalId,
  eventTitle,
  forWhom,
  groupClassSeriesExternalId,
  isOpen,
  onClose,
  onCompletion,
  patientExternalId,
}: EditGroupEventBottomSheetModalProps) => {
  const baseTestID = joinTestIDs(
    ModalTestID.EditGroupEvent,
    eventExternalId ?? groupClassSeriesExternalId,
    ModalTestID.Modal,
  );

  const [isDeclineAlertOpen, setIsDeclineAlertOpen] = useState<boolean>(false);
  const [isUnregisterAlertOpen, setIsUnregisterAlertOpen] =
    useState<boolean>(false);

  // Unregister
  const handleUnregisterPress = () => {
    setIsUnregisterAlertOpen(true);
  };

  const handleUnregisterAlertDismiss = () => {
    setIsUnregisterAlertOpen(false);
  };

  const handleUnregisterCompletion = () => {
    handleUnregisterAlertDismiss();
    onCompletion();
  };

  // Decline
  const handleDeclinePress = () => {
    setIsDeclineAlertOpen(true);
  };

  const handleDeclineAlertDismiss = () => {
    setIsDeclineAlertOpen(false);
  };

  const handleDeclineCompletion = () => {
    handleDeclineAlertDismiss();
    onCompletion();
  };

  if (isEmpty(groupClassSeriesExternalId)) {
    return null;
  }

  return (
    <BottomSheetModal
      dismissAccessibilityLabel={
        EditGroupEventRegistrationContent.DismissAccessibilityLabel
      }
      isOpen={isOpen}
      onRequestClose={() => onClose()}
      testID={baseTestID}
      title={EditGroupEventRegistrationContent.Title}
    >
      <VStack gap={12}>
        <Text
          testID={joinTestIDs(baseTestID, `${TextTestID.Description} `)}
          variant="body"
        >
          {EditGroupEventRegistrationContent.Description}
        </Text>
        <VStack alignItems="center" gap={12} justifyContent="center">
          <Button
            isFullWidth
            leftIcon={FAIconName.UserSlash}
            onPress={handleUnregisterPress}
            testID={joinTestIDs(baseTestID, "unregister")}
            title={EditGroupEventRegistrationContent.UnregisterCTA}
            variant="secondary"
          />
          {!isEmpty(eventExternalId) && (
            <Button
              isFullWidth
              leftIcon={FAIconName.Trash}
              onPress={handleDeclinePress}
              testID={joinTestIDs(baseTestID, "decline")}
              title={EditGroupEventRegistrationContent.DeclineCTA}
              variant="secondary"
            />
          )}
        </VStack>
      </VStack>
      <UnregisterGroupConfirmationAlert
        forWhom={forWhom}
        groupClassSeriesExternalId={groupClassSeriesExternalId}
        isOpen={isUnregisterAlertOpen}
        onCompletion={handleUnregisterCompletion}
        onDismiss={handleUnregisterAlertDismiss}
        patientExternalId={patientExternalId}
      />
      {isNotNilNotEmpty(eventExternalId) && (
        <DeclineGroupEventConfirmationAlert
          eventTitle={eventTitle}
          groupClassExternalId={eventExternalId}
          isOpen={isDeclineAlertOpen}
          onCompletion={handleDeclineCompletion}
          onDismiss={handleDeclineAlertDismiss}
          patientExternalId={patientExternalId}
        />
      )}
    </BottomSheetModal>
  );
};

export { EditGroupEventRegistrationContent };
export type { EditGroupEventBottomSheetModalProps };
export default EditGroupEventBottomSheetModal;
